/**
 * 多语言数据处理工具函数
 * 根据当前语言状态返回对应的数据字段
 */

/**
 * 获取多语言字段值
 * @param {Object} item - 数据项
 * @param {string} fieldName - 字段名（不包含语言前缀）
 * @param {string} currentLocale - 当前语言 ('zh' | 'en')
 * @returns {string} - 对应语言的字段值
 */
export function getLocalizedField(item, fieldName, currentLocale) {
  if (!item || !fieldName) return ''
  
  if (currentLocale === 'en') {
    // 英文状态：优先使用en开头的字段，如果不存在则使用中文字段作为fallback
    const enFieldName = `en${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`
    return item[enFieldName] || item[fieldName] || ''
  } else {
    // 中文状态：直接使用原字段名
    return item[fieldName] || ''
  }
}

/**
 * 处理产品数据，返回当前语言对应的显示数据
 * @param {Object} product - 产品数据对象
 * @param {string} currentLocale - 当前语言
 * @returns {Object} - 处理后的产品数据
 */
export function processProductData(product, currentLocale) {
  if (!product) return null
  
  return {
    ...product,
    // 显示字段映射
    displayName: getLocalizedField(product, 'shopName', currentLocale),
    displayContent: getLocalizedField(product, 'shopContent', currentLocale),
    displayPrice: product.shopNowPrice || product.shopOriginalPrice || 0
  }
}

/**
 * 处理新闻数据，返回当前语言对应的显示数据
 * @param {Object} news - 新闻数据对象
 * @param {string} currentLocale - 当前语言
 * @returns {Object} - 处理后的新闻数据
 */
export function processNewsData(news, currentLocale) {
  if (!news) return null
  
  return {
    ...news,
    // 显示字段映射
    displayName: getLocalizedField(news, 'name', currentLocale),
    displayContent: getLocalizedField(news, 'content', currentLocale)
  }
}

/**
 * 批量处理产品列表数据
 * @param {Array} products - 产品列表
 * @param {string} currentLocale - 当前语言
 * @returns {Array} - 处理后的产品列表
 */
export function processProductList(products, currentLocale) {
  if (!Array.isArray(products)) return []
  
  return products.map(product => processProductData(product, currentLocale))
}

/**
 * 批量处理新闻列表数据
 * @param {Array} newsList - 新闻列表
 * @param {string} currentLocale - 当前语言
 * @returns {Array} - 处理后的新闻列表
 */
export function processNewsList(newsList, currentLocale) {
  if (!Array.isArray(newsList)) return []
  
  return newsList.map(news => processNewsData(news, currentLocale))
}
